<template>
    <div
        :class="[
            type === 'textarea' ? 'app-textarea' : 'app-input',
            inputSize ? 'app-input--' + inputSize : '',
            {
                'is-disabled': inputDisabled,
                'is-readonly': readonly,
                'app-input-group': $slots.prepend || $slots.append,
                'app-input-group--select': selectInput,
                'app-input-group--append': $slots.append,
                'app-input-group--prepend': $slots.prepend,
                'app-input--prefix': $slots.prefix || prefixIcon,
                'app-input--suffix': $slots.suffix || suffixIcon,
                'mini': size==='mini'
                // 'app-input--suffix': $slots.suffix || suffixIcon || clearable || 
            }
    ]">
        <template v-if="type !== 'textarea' && !selectInput">
            <div v-if="$slots.prepend" class="app-input-group__prepend">
                <slot name="prepend"/>
            </div>
            <input
                ref="input"
                :type="type"
                v-bind="$attrs"
                :aria-label="label"
                :readonly="readonly"
                :disabled="inputDisabled"
                :placeholder="placeholder"
                class="app-input__inner"
                @input="handleInput"
                @change="handleChange"
                @focus="handleFocus"
                @blur="handleBlur">
            <div v-if="$slots.append" class="app-input-group__append">
                <slot name="append"/>
            </div>
            <span v-if="$slots.prefix || prefixIcon" class="app-input__prefix">
                <slot name="prefix"/>
                <i v-if="prefixIcon" :class="prefixIcon" class="app-input__icon"/>
            </span>
            <span v-if="$slots.suffix || suffixIcon" class="app-input__suffix">
                <slot name="suffix"/>
                <i v-if="suffixIcon" :class="suffixIcon" class="app-input__icon"/>
            </span>
        </template>
        <textarea
            v-else-if="type == 'textarea' && !selectInput"
            ref="textarea"
            v-bind="$attrs"
            :readonly="readonly"
            :disabled="inputDisabled"
            :aria-label="label"
            :placeholder="placeholder"
            rows="3"
            class="app-textarea__inner"
            @input="handleInput"
            @focus="handleFocus"
            @blur="handleBlur"
            @change="handleChange"/>
        <template v-else-if="type !== 'textarea' && selectInput">
            <div v-if="$slots.prepend" class="app-input-group__prepend">
                <slot name="prepend"/>
            </div>
            <input
                ref="select"
                v-bind="$attrs"
                :aria-label="label"
                :disabled="inputDisabled"
                :readonly="!filterable"
                :placeholder="placeholder"
                class="app-input__inner select-input"
                type="text"
                tabindex="0"
                @click="handleClick"
                @mousedown="handleMousedown"
                @input="handleInput"
                @change="handleChange"
                @focus="handleFocus"
                @blur="handleBlur"
                @keyup.enter.stop.prevent="$emit('enter')"
                @keyup.esc.stop="$emit('click', false)"
                @keyup.up.stop.prevent="$emit('up')"
                @keyup.down.stop.prevent="$emit('down')"
                @keydown.down.prevent=""
                @keydown.up.prevent=""
                @keydown.enter.prevent="">
            <div
                :class="{'select-open': isOpen}"
                class="app-input-group__append select-input-arrow"
                @mousedown.stop="handleArrowMousedown"
                @click="handleArrowClick">
                <slot name="append"/>
            </div>
            <span v-if="$slots.prefix || prefixIcon" class="app-input__prefix">
                <slot name="prefix"/>
                <i v-if="prefixIcon" :class="prefixIcon" class="app-input__icon"/>
            </span>
            <span v-if="$slots.suffix || suffixIcon" class="app-input__suffix">
                <slot name="suffix"/>
                <i v-if="suffixIcon" :class="suffixIcon" class="app-input__icon"/>
            </span>
        </template>
    </div>
</template>

<script>
import emitter from '../../utilities/emitter'
import { setTimeout } from 'timers'

export default {
    name: 'app-input',
    mixins: [emitter],
    inject: {
        AppForm: {
            default: ''
        },
        AppFormItem: {
            default: ''
        }
    },
    props: {
        value: [String, Number],
        form: String,
        validateEvent: {
            type: Boolean,
            default: true
        },
        type: {
            type: String,
            default: 'text'
        },
        selectInput: {
            type: Boolean,
            default: false
        },
        suffixIcon: String,
        prefixIcon: String,
        size: String,
        label: String,
        disabled: Boolean,
        readonly: Boolean,
        isOpen: Boolean,
        filterable: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: ''
        },
    },
    data() {
        return {
            focused: false,
            timeoutId: null,
            arrowMousedown: false,
            shouldEmitClick: true
        }
    },
    computed: {
        validateState() {
            return this.AppFormItem ? this.AppFormItem.validateState : ''
        },
        nativeInputValue() {
            return this.value === null || this.value === undefined
                ? ''
                : String(this.value)
        },
        inputSize() {
            return this.size
        },
        inputDisabled() {
            return this.disabled || (this.appForm || {}).disabled;
        }
    },
    created() {
        this.$on('inputSelect', this.select)
    },
    mounted() {
        this.setNativeInputValue()
        this.updateIconOffset()
    },
    updated() {
        this.$nextTick(this.updateIconOffset)
    },
    methods: {
        getInput() {
            return this.$refs.input || this.$refs.textarea || this.$refs.select
        },
        setNativeInputValue() {
            const input = this.getInput()
            if (!input) return
            if (input.value === this.nativeInputValue) return
            input.value = this.nativeInputValue
        },
        handleBlur(evt) {
            if (this.selectInput) {
                if(!this.arrowMousedown) {
                    clearTimeout(this.timeoutId)
                    this.timeoutId = setTimeout( ()=> {
                        this.setNativeInputValue()
                        this.$emit('blur', evt)
                        if (this.validateEvent) {
                            this.dispatch('app-form-item', 'app.form.blur', [this.value])
                        }
                    }, 100)
                }
            }
            else {
                this.$emit('blur', evt)
                if (this.validateEvent) {
                    this.dispatch('app-form-item', 'app.form.blur', [this.value])
                }
            }
        },
        handleChange(evt) {
            this.$emit('change', evt.target.value)
        },
        handleFocus(evt) {
            if (this.selectInput) {
                clearTimeout(this.timeoutId)
                this.timeoutId = setTimeout( ()=>{
                    this.$emit('focus', evt)
                }, 100)
            }
            else {
                this.$emit('focus', evt)
            }
        },
        handleClick() {
            let prevIsOpen = this.isOpen
            clearTimeout(this.timeoutId)
            if (this.shouldEmitClick) {
                this.timeoutId = setTimeout( ()=>{
                    if (prevIsOpen !== this.isOpen)
                        this.$emit('click', this.isOpen)
                    else
                        this.$emit('click', !this.isOpen)
                }, 100)
            }
            this.shouldEmitClick = true
        },
        handleArrowClick() {
            this.focus();
            this.arrowMousedown = false
            this.handleClick()
        },
        handleArrowMousedown() {
            this.arrowMousedown = true
        },
        handleMousedown() {
            if (this.$refs['select'] !== document.activeElement)
                this.shouldEmitClick = false
        },
        handleInput(evt) {
            if (this.selectInput && this.filterable) {
                this.$emit('filter', evt.target.value)
            }
            else {
                if (evt.target.value === this.nativeInputValue) return
                this.$emit('input', evt.target.value)
                this.$nextTick(this.setNativeInputValue)
            }
        },
        calcIconOffset(place) {
            let elList = [].slice.call(this.$el.querySelectorAll(`.app-input__${place}`) || [])
            if (!elList.length) return
            let el = null
            for (let i = 0; i < elList.length; i++) {
                if (elList[i].parentNode === this.$el) {
                    el = elList[i]
                    break
                }
            }
            if (!el) return
            const pendantMap = {
                suffix: 'append',
                prefix: 'prepend'
            }

            const pendant = pendantMap[place]
            if (this.$slots[pendant]) {
                el.style.transform = `translateX(${
                    place === 'suffix' ? '-' : ''
                }${
                    this.$el.querySelector(`.app-input-group__${pendant}`)
                        .offsetWidth
                }px)`
            }
            else {
                el.removeAttribute('style')
            }
        },
        updateIconOffset() {
            this.calcIconOffset('prefix');
            this.calcIconOffset('suffix');
        },
        clear() {
            this.$emit('input', '');
            this.$emit('change', '');
            this.$emit('clear');
        },
        select() {
            this.getInput().select()
        },
        focus() {
            this.getInput().focus();
        },
        blur() {
            this.getInput().blur();
        }
    },
    watch: {
        nativeInputValue() {
            this.setNativeInputValue()
        },
        value(val) {
            this.dispatch('app-form-item', 'app.form.change', [val])
        }
    },
}
</script>

<style lang="stylus" scoped>
    .app-form-item
        &.is-success
            .app-input-group__append,
            .app-input input,
            .app-textarea__inner
                border-color: #67c23a
        &.is-error
            .app-input-group__append,
            .app-input__inner,
            .app-textarea__inner
                border-color: #f56c6c !important
        .app-input-group--select
            max-width: 100%

    .app-input
        position: relative
        font-size: 14px
        display: inline-block
        width: 100%
        &.is-disabled,
        &.is-readonly
            .app-input__inner
                background-color: #f5f7fa
                border-color: #E4E7ED
                color: #C0C4CC
        &.is-disabled
            .app-input__inner
                cursor: not-allowed

    .app-input-group
        line-height: normal
        display: inline-table
        width: 100%
        border-collapse: separate
        border-spacing: 0

    .app-input-group__append,
    .app-input-group__prepend
        background-color: #f5f7fa
        color: #909399
        vertical-align: middle
        display: table-cell
        position: relative
        border: 1px solid #dcdfe6
        border-radius: 6px
        padding: 0 20px
        width: 1px
        white-space: nowrap
        padding: 0px 15px

    .app-input-group__prepend
        border-top-right-radius: 0
        border-bottom-right-radius: 0
        border-right: 0
    .app-input-group--append
        .app-input__inner
            border-top-right-radius: 0
            border-bottom-right-radius: 0

    .app-input-group__append
        border-left: 0
        border-top-left-radius: 0
        border-bottom-left-radius: 0
    .app-input-group--prepend
        .app-input__inner
            border-top-left-radius: 0
            border-bottom-left-radius: 0

    .app-input--small
        font-size: 13px
        width: auto
        .app-input__icon,
        .app-input__inner
            height: 32px
            line-height: 32px

    .app-input__prefix,
    .app-input__suffix
        position: absolute
        top: 2px
        -webkit-transition: all 0.3s
        height: 100%
        color: #c0c4cc
        text-align: center

    .app-input--prefix
        .app-input__inner
            padding-left: 30px
    .app-input__prefix
        left: 10px
    .app-input--suffix
        .app-input__inner
            padding-right: 30px
    .app-input__suffix
        right: 5px

    .app-input-icon,
    .app-input__prefix
        -webkit-transition: all .3s
        -o-transition: all .3s
        transition: all .3s

    .app-input__icon
        height: 100%
        width: 25px
        text-align: center
        line-height: 40px

    .app-input__inner
        -webkit-appearance: none
        background-color: #FFF
        background-image: none
        border-radius: 6px
        border: 1px solid #DCDFE6
        -webkit-box-sizing: border-box
        box-sizing: border-box
        color: #606266
        display: inline-block
        font-size: inherit
        height: 40px
        line-height: normal
        outline: 0
        padding: 0 15px
        -webkit-transition: border-color .2s cubic-bezier(.645,.045,.355,1)
        -o-transition: border-color .2s cubic-bezier(.645,.045,.355,1)
        transition: border-color .2s cubic-bezier(.645,.045,.355,1)
        width: 100%
        &:focus
            border-color: #1383ff
            color: #232c3f
        &:focus + .select-input-arrow
            border-color: #1383ff
        &::placeholder,
        &::-webkit-input-placeholder
            color: #c0c4cc

    .app-textarea
        display: inline-block
        width: 100%
        vertical-align: bottom
        font-size: 14px
        &.is-disabled,
        &.is-readonly
            .app-textarea__inner
                background-color: #f5f7fa
                border-color: #E4E7ED
                color: #C0C4CC
        &.is-disabled
            .app-textarea__inner
                cursor: not-allowed

    .app-textarea__inner
        display: block
        resize: vertical
        padding: 5px 15px
        line-height: 1.5
        -webkit-box-sizing: border-box
        box-sizing: border-box
        width: 100%
        font-size: inherit
        color: #606266
        background-color: #FFF
        background-image: none
        border: 1px solid #DCDFE6
        border-radius: 6px
        -webkit-transition: border-color .2s cubic-bezier(.645,.045,.355,1)
        -o-transition: border-color .2s cubic-bezier(.645,.045,.355,1)
        transition: border-color .2s cubic-bezier(.645,.045,.355,1)
        &:focus
            outline: 0
            border-color: #409EFF

    .app-input-group--select
        max-width: max-content

    .app-input__inner.select-input
        border-right: 0
        cursor: pointer
        padding-right: 0

    .select-input-arrow
        background-color: white
        cursor: pointer
        -webkit-transition: border-color .2s cubic-bezier(.645,.045,.355,1)
        -o-transition: border-color .2s cubic-bezier(.645,.045,.355,1)
        transition: border-color .2s cubic-bezier(.645,.045,.355,1)
        i
            display: inline-block
            transition: .2s
            margin-top: 4px
            margin-bottom: 0
    
    .select-open i
        transform: rotate(-180deg)
        margin-top: 0
        margin-bottom: 3px

    .mini
        .app-input__inner
            height: 28px
            padding-right: 0px
            font-size: 12px
            width: 50px
</style>